// Hook: useCampaignDrafts
// Purpose: Manages CRUD for campaign drafts scoped to a namespace, including
// loading, saving, deleting, and loading into the active form. Honors premium
// access checks and provides UI messaging state.
import { useState, useEffect } from 'react';
import { SERVER_URL } from '../../../api/core';
import { auth } from '../../../firebase/firebase';

// Helper function to check if user has premium access
// REMOVED - No longer needed, as we get isPremium from props
/*
export function checkPremiumAccess() {
  try {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const isPremium = userData?.planLevel === 'premium' || 
                     userData?.subscriptionStatus?.currentPlanLevel === 'premium';
    return isPremium;
  } catch (e) {
    console.error('Error checking premium access:', e);
    return false;
  }
}
*/

export const useCampaignDrafts = (
  formData, 
  updateFormData, 
  clearFormCache, 
  surveyQuestions, 
  setSurveyQuestions,
  setAiGeneratedFields,
  selectedTheme,
  setSelectedTheme,
  gradientColors,
  setGradientColors,
  gradientDirection,
  setGradientDirection,
  hexText,
  setHexText,
  previewImage,
  setPreviewImage,
  hasExplainerVideo,
  setHasExplainerVideo,
  explainerVideo,
  setExplainerVideo,
  setCurrentStep,
  setIsDraftsOpen,
  namespaceId,
  isPremium
) => {
  const [drafts, setDrafts] = useState([]);
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(true);
  const [selectedDraftId, setSelectedDraftId] = useState(null);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);
  const [isEditingDraft, setIsEditingDraft] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [error, setError] = useState('');
  const [deleteMessage, setDeleteMessage] = useState(null);

  useEffect(() => {
    // Fetch drafts only when namespaceId is available and user is premium
    if (namespaceId && isPremium) {
      fetchDrafts();
    } else {
      // If not premium or no namespace, ensure loading is false and drafts are empty
      setDrafts([]);
      setIsLoadingDrafts(false);
    }
  }, [namespaceId, isPremium]);

  const fetchDrafts = async () => {
    setIsLoadingDrafts(true); // Set loading true when starting fetch
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${SERVER_URL}/draftCampaign/drafts?namespaceId=${namespaceId}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch drafts');
      }

      const data = await response.json();
      setDrafts(data);
    } catch (err) {
      console.error('Error fetching drafts:', err);
    } finally {
      setIsLoadingDrafts(false);
    }
  };

  const handleSaveDraft = async () => {
    // Check premium access before attempting to save a draft
    if (!isPremium) {
      setError({
        type: 'error',
        message: 'Campaign drafts are a premium feature. Please upgrade your plan to save drafts.'
      });
      setTimeout(() => setError(null), 5000);
      return;
    }
    
    if (!formData.name.trim()) {
      setError('Please enter a campaign name to save as draft');
      setTimeout(() => setError(null), 3000);
      return;
    }
    
    if (!namespaceId) {
      setError({
        type: 'error',
        message: 'No namespace selected. Please select a namespace before saving a draft.'
      });
      setTimeout(() => setError(null), 3000);
      return;
    }

    setIsSavingDraft(true);

    try {
      const idToken = await auth.currentUser.getIdToken();
      
      // Get custom colors if the theme is 'custom'
      const customColors = selectedTheme === 'custom' ? {
        gradientColors,
        gradientDirection,
        textColor: hexText
      } : null;
      
      
      const draftData = {
        name: formData.name,
        title: formData.title,
        description: formData.description,
        theme: selectedTheme,
        customColors: customColors,
        campaignImage: previewImage,
        surveyQuestions: Array.isArray(surveyQuestions) 
          ? JSON.stringify(surveyQuestions.map(q => q.question)) 
          : JSON.stringify([]),
        hasExplainerVideo: hasExplainerVideo,
        explainerVideo: explainerVideo,
        namespaceId: namespaceId // Add namespace ID to draft data
      };

      const response = await fetch(isEditingDraft 
        ? `${SERVER_URL}/draftCampaign/drafts/${selectedDraftId}?namespaceId=${namespaceId}`
        : `${SERVER_URL}/draftCampaign/drafts?namespaceId=${namespaceId}`, {
        method: isEditingDraft ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(draftData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403) {
          throw new Error(errorData.error || 'You do not have permission to save drafts in this namespace.');
        } else {
          throw new Error(errorData.message || errorData.error || 'Failed to save draft');
        }
      }

      await fetchDrafts();
      // clearFormCache(); // Don't clear form cache, this is causing the data to be lost
      
      setError({
        type: 'success',
        message: isEditingDraft ? 'Draft updated successfully!' : 'Draft saved successfully!'
      });
      
      // Close the drafts modal
      setTimeout(() => {
        setIsDraftsOpen(false);
        setError(null);
      }, 2000);
    } catch (err) {
      console.error('Error saving draft:', err);
      
      // Create a more user-friendly error message
      let errorMessage = 'Unable to save draft at this time. Please try again later.';
      
      // Add more specific messages for common errors
      if (err.message.includes('permission')) {
        errorMessage = 'You don\'t have permission to save drafts in this namespace.';
      } else if (err.message.includes('network') || err.message.includes('offline')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError({
        type: 'error',
        message: errorMessage
      });
      
      // Automatically clear the error after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleLoadDraft = async (draftId) => {
    // Check premium access before loading a draft
    if (!isPremium) {
      setError({
        type: 'error',
        message: 'Campaign drafts are a premium feature. Please upgrade your plan to load drafts.'
      });
      setTimeout(() => setError(null), 5000);
      return;
    }
    
    try {
      setSelectedDraftId(draftId);
      setIsEditingDraft(true);
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${SERVER_URL}/draftCampaign/drafts/${draftId}?namespaceId=${namespaceId}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403) {
          throw new Error(errorData.error || 'You do not have permission to access this draft.');
        } else {
          throw new Error(errorData.error || 'Failed to load draft');
        }
      }

      const draft = await response.json();
      
      // Load the draft data, including any legacy business fields that might exist
      // but won't be included in future submissions
      updateFormData({
        name: draft.name || '',
        title: draft.title || '',
        description: draft.description || '',
        // Keep these fields for backward compatibility with existing drafts
        businessName: draft.businessName || '',
        website: draft.website || '',
        email: draft.email || '',
        phone: draft.phone || ''
      });
      
      // Set theme if available
      if (draft.theme) {
        setSelectedTheme(draft.theme);
      }
      
      // Load custom colors if available
      if (draft.customColors) {
        setGradientColors(draft.customColors.gradientColors || {
          from: '#1a365d',
          via: '#3182ce',
          to: '#2c5282'
        });
        setGradientDirection(draft.customColors.gradientDirection || 'br');
        setHexText(draft.customColors.textColor || '#ffffff');
      }
      
      // Load campaign image if available
      if (draft.campaignImage) {
        setPreviewImage(draft.campaignImage);
      }
      
      // Load explainer video if available
      if (draft.hasExplainerVideo) {
        setHasExplainerVideo(true);
        setExplainerVideo(draft.explainerVideo || null);
      } else {
        setHasExplainerVideo(false);
        setExplainerVideo(null);
      }
      
      // Handle surveyQuestions which might be a string, array, or null
      let questions = [];
      if (draft.surveyQuestions) {
        if (typeof draft.surveyQuestions === 'string') {
          try {
            questions = JSON.parse(draft.surveyQuestions);
          } catch (e) {
            console.error('Error parsing surveyQuestions:', e);
            questions = [];
          }
        } else if (Array.isArray(draft.surveyQuestions)) {
          questions = draft.surveyQuestions;
        }
      }
      
      setSurveyQuestions(questions.map((question, index) => ({
        id: index + 1,
        question: typeof question === 'string' ? question : ''
      })));
      setIsDraftsOpen(false);
      
      // Clear AI indicators when loading a draft
      setAiGeneratedFields({});
      
      // Show success message
      setError({
        type: 'success',
        message: `Draft "${draft.name}" loaded successfully!`
      });
      setTimeout(() => setError(null), 3000);
    } catch (err) {
      console.error('Error loading draft:', err);
      setError(err.message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDeleteDraft = async (draftId) => {
    if (!draftId) return;
    
    // Check premium access before deleting a draft
    if (!isPremium) {
      setError({
        type: 'error',
        message: 'Campaign drafts are a premium feature. Please upgrade your plan to manage drafts.'
      });
      setTimeout(() => setError(null), 5000);
      return;
    }
    
    setIsDeletingDraft(true);
    setSelectedDraftId(draftId);
    setDeleteMessage(null);

    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${SERVER_URL}/draftCampaign/drafts/${draftId}?namespaceId=${namespaceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403) {
          throw new Error(errorData.error || 'You do not have permission to delete this draft. Admin access required.');
        } else {
          throw new Error(errorData.error || 'Failed to delete draft');
        }
      }

      await fetchDrafts();
      setIsDraftsOpen(false);
      setSelectedDraftId(null);
      setIsEditingDraft(false);
      
      setDeleteMessage({
        type: 'success',
        message: 'Draft deleted successfully!'
      });
      setTimeout(() => setDeleteMessage(null), 3000);
      
      // Clear any existing error messages
      setError(null);
    } catch (err) {
      console.error('Error deleting draft:', err);
      setDeleteMessage({
        type: 'error',
        message: err.message || 'Error deleting draft'
      });
    } finally {
      setIsDeletingDraft(false);
    }
  };

  return {
    drafts,
    isLoadingDrafts,
    selectedDraftId,
    setSelectedDraftId,
    isDeletingDraft,
    isEditingDraft,
    setIsEditingDraft,
    isSavingDraft,
    error,
    setError,
    deleteMessage,
    setDeleteMessage,
    fetchDrafts,
    handleSaveDraft,
    handleLoadDraft,
    handleDeleteDraft
  };
}; 